import {createContext, use, useRef, type RefObject, type ReactNode} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createIssueCommentSlice, type IssueCommentSlice, type IssueCommentsSliceOpts} from './slices/issue-comments'
import {createMarkersSlice, type MarkersInfo, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createPendingReviewSlice,
  type PendingReview,
  type PendingReviewSlice,
  type PendingReviewSliceOpts,
} from './slices/pending-review'
import {createPreferencesSlice, type PreferencesSlice, type PreferencesSliceOpts} from './slices/preferences'
import type {PullRequestState} from '../types'
import {buildNormalizedMarkers, buildPendingReview} from '../helpers/diff-helpers'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {CommentsPreference} from '@github-ui/diff-view-settings/types'

/** Minimal PR context needed by the layout store (no diff-specific data) */
export type LayoutPullRequestInfo = {
  basePath: string
  id: string
  number: number
  state: PullRequestState
}

export type LayoutStoreState = IssueCommentSlice &
  MarkersSlice &
  PendingReviewSlice &
  PreferencesSlice & {
    layoutPullRequest: LayoutPullRequestInfo
    /** True once the store has been seeded with markers/pendingReview.
     *  Prevents stale re-hydration when PullRequestStoreProvider remounts. */
    layoutStoreSeeded: boolean
    layoutStoreActions: {
      hydrateMarkers: (markers: MarkersInfo) => void
      hydratePendingReview: (pendingReview: PendingReview) => void
      hydratePreferences: (preferences: PreferencesSliceOpts) => void
    }
  }

type LayoutStoreInitialState = IssueCommentsSliceOpts &
  MarkersSliceOpts &
  PendingReviewSliceOpts &
  PreferencesSliceOpts & {
    layoutPullRequest: LayoutPullRequestInfo
    layoutStoreSeeded: boolean
  }

type LayoutStoreInstance = ReturnType<typeof createLayoutStore>

const LayoutStoreContext = createContext<LayoutStoreInstance | null>(null)

export function useLayoutStore<T>(selector: (state: LayoutStoreState) => T): T {
  const store = use(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider in the tree')
  return useStore(store, selector)
}

export interface LayoutStoreProviderProps {
  children: ReactNode
  basePath: string
  pullRequestId: string
  pullRequestNumber: number
  pullRequestState: PullRequestState
  /** Ref populated by FilesChangedDataCapture (rendered as an earlier sibling) with
   *  child-route data so the store can be created with real markers/pendingReview
   *  on the very first render — critical for SSR. */
  initialFilesDataRef?: RefObject<FilesRoutePayload | undefined>
  /** Shared ref that tracks whether the store has been seeded (SSR or soft-nav).
   *  Set to true when the SSR path seeds, read by LayoutStoreSeeder to avoid re-seeding. */
  seededRef?: RefObject<boolean>
}

export function LayoutStoreProviderWrapper({
  shouldAddLayoutStoreWrapper,
  children,
  basePath,
  pullRequestId,
  pullRequestNumber,
  pullRequestState,
}: {
  shouldAddLayoutStoreWrapper: boolean
  children: ReactNode
  basePath: string
  pullRequestId: string
  pullRequestNumber: number
  pullRequestState: PullRequestState
}) {
  if (!shouldAddLayoutStoreWrapper) {
    return <>{children}</>
  }
  return (
    <LayoutStoreProvider
      basePath={basePath}
      pullRequestId={pullRequestId}
      pullRequestNumber={pullRequestNumber}
      pullRequestState={pullRequestState}
    >
      {children}
    </LayoutStoreProvider>
  )
}
export function LayoutStoreProvider({
  children,
  basePath,
  pullRequestId,
  pullRequestNumber,
  pullRequestState,
  initialFilesDataRef,
  seededRef,
}: LayoutStoreProviderProps) {
  const storeRef = useRef<LayoutStoreInstance | null>(null)

  if (storeRef.current === null) {
    // Read the ref populated by FilesChangedDataCapture (sibling rendered before this component).
    // React renders siblings in order, so the ref is already populated during SSR.
    // eslint-disable-next-line react-hooks/refs
    const filesData = initialFilesDataRef?.current
    const initialMarkers = filesData ? (buildNormalizedMarkers(filesData.markers, filesData.diffSummaries) ?? {}) : {}
    const initialPendingReview = filesData ? buildPendingReview(filesData.viewerPendingReview) : {comments: []}
    const initialCommentsPreference = filesData
      ? filesData.user.viewSettings.commentsPreference
      : CommentsPreference.Visible

    // Mark as seeded so LayoutStoreSeeder won't re-seed on subsequent soft-navs
    // eslint-disable-next-line react-hooks/refs
    if (filesData && seededRef) seededRef.current = true

    // eslint-disable-next-line react-hooks/refs
    const wasSeeded = !!filesData

    storeRef.current = createLayoutStore({
      issueCommentsCount: 0,
      markers: initialMarkers,
      pendingReview: initialPendingReview,
      layoutStoreSeeded: wasSeeded,
      commentsPreference: initialCommentsPreference,
      layoutPullRequest: {
        basePath,
        id: pullRequestId,
        number: pullRequestNumber,
        state: pullRequestState,
      },
    })
  }

  // We disable ESLint refs here as we need our Zustand stores to be a stable reference
  // Using a useState to store our Zustand store will cause unnecessary re-renders and downstream performance issues
  // eslint-disable-next-line react-hooks/refs
  return <LayoutStoreContext value={storeRef.current}>{children}</LayoutStoreContext>
}

const createLayoutStore = (initState: LayoutStoreInitialState) => {
  return createStore<LayoutStoreState>()(
    devtools(
      (...a) => ({
        ...createIssueCommentSlice(initState)(...a),
        ...createMarkersSlice(initState)(...a),
        ...createPendingReviewSlice(initState)(...a),
        ...createPreferencesSlice(initState)(...a),
        layoutPullRequest: initState.layoutPullRequest,
        layoutStoreSeeded: initState.layoutStoreSeeded,
        layoutStoreActions: {
          hydrateMarkers: (markers: MarkersInfo) => {
            const [set] = a
            set({markers}, undefined, 'hydrateMarkers')
          },
          hydratePendingReview: (pendingReview: PendingReview) => {
            const [set] = a
            set({pendingReview}, undefined, 'hydratePendingReview')
          },
          hydratePreferences: (preferences: PreferencesSliceOpts) => {
            const [set] = a
            set(
              {commentsPreference: preferences.commentsPreference ?? CommentsPreference.Visible},
              undefined,
              'hydratePreferences',
            )
          },
        },
      }),
      {name: 'layout-store'},
    ),
  )
}

/**
 * Returns the raw zustand store instance for imperative access (e.g. from PullRequestStoreProvider).
 * Prefer useLayoutStore with selectors in components.
 */
export function useLayoutStoreInstance(): LayoutStoreInstance {
  const store = use(LayoutStoreContext)
  if (!store) throw new Error('Missing LayoutStoreProvider in the tree')
  return store
}
